/**
 * Block textures — an authored picture where one exists, numbers everywhere
 * else (T84, then T143).
 *
 * 🔴 **This file used to say "generated from numbers rather than painted", flat
 * out.** That stopped being true on **2026-08-13**, when ten blocks got real
 * images. Both paths are live and the split is deliberate:
 *
 * | | |
 * |---|---|
 * | **`data/block-tiles.json`** | A drawn picture, 64 px, for the ten blocks of the test area. **Used if present** |
 * | **`data/block-textures.json`** | The recipe below. Everything else, and the fallback |
 *
 * 🎯 **Why the change: haugur, 2026-08-13** — *"im not satisfied with the few
 * blocks we have designed, design is flat, grainy, not visually pleasing… i
 * want it to look better than minecraft if possible."* ⚠️ **He was right and it
 * was not the drawing's fault.** The recipe below can only modulate the
 * *brightness* of one base colour, so a tile is that colour lighter and darker
 * and nothing else — flat by construction, and grainy because per-texel noise
 * at 16 px reads as grain rather than as material. **The ceiling was the
 * system.**
 *
 * ## Why the recipe is still here, and still the default
 *
 * `data/block-textures.json` says *what is in* each block face — how many
 * crowberry clumps, how big a moss lobe is, how much of the tile is bare stone
 * — and this builds the pixels from it. Nobody has to open a paint program to
 * change how the world looks, which is the same argument as every other data
 * file in the project (D2, `Development/CLAUDE.md` #7).
 *
 * It also keeps the texture **traceable**. `lyng`'s recipe is literally
 * Náttúrufræðistofnun's published cover table for habitat class L6.4 — 12.3%
 * crowberry, 14% bare stone, 5.6% lichen — so the texture *is* the research
 * rather than an impression of it.
 *
 * ## ⚠️ Why this lives in `shared` and imports nothing
 *
 * So the game and the preview tool run **the same code**. A separate preview
 * that reimplemented the generator would drift, and haugur would be judging
 * pictures of a texture the game does not draw — which is the same failure as
 * verifying an API instead of the app.
 */

import { BLOCKS, blockAppearance, blockCutout, blockIsCross } from './blocks.ts';
import { RIPPLE_FRAMES, rippleFrame } from './ripple.ts';
import { statesOf } from './states.ts';
import textureData from '../data/block-textures.json' with { type: 'json' };
import tileData from '../data/block-tiles.json' with { type: 'json' };

/**
 * 🌾 **The blocks that have a grass-band side** — #813, haugur's "A".
 *
 * 🎯 *"since its grass the sides should prob be grass also"* — a cut edge, a
 * dug edge and a bank top show the field's grass along the top quarter.
 * 🔴 **Only the TOP of the ground**: the world puts three more metres of the
 * same block under every surface block (`SOIL_DEPTH`), so a band on every
 * side would stripe a bank once a metre. The mesher asks {@link edgeLayer}.
 *
 * ⚠️ **Built here, at the top, on purpose**: `LAYERS` is built when this module
 * loads and calls `tilePlans()`, so anything it reads must already exist.
 */
const EDGE_KEYS: ReadonlySet<string> = new Set(
  Object.entries(tileData.faces as Record<string, Record<string, string>>)
    .filter(([, faces]) => 'edge' in faces)
    .map(([key]) => key),
);

/**
 * Texels along one edge of a tile, and therefore how coarse the world looks.
 *
 * 🔴 **64 since 2026-08-13, up from 16.** One texel is now **1.6 cm** on a 1 m
 * block rather than 6.25 cm.
 *
 * 🎯 **haugur:** *"increasing the block size is fine by me, as big as it needs
 * to be without hurting that overall game performance."* ⚠️ **And it fits with
 * his OTHER choice — crisp** (V26). Crisp means you see the tile's real pixels,
 * so at 16 you see 16 hard squares per block and at 64 you see 64 finer ones:
 * **still unmistakably a block, with sixteen times the detail in it.** That is
 * what an HD texture pack does to Minecraft, which is what he asked for.
 *
 * **Memory is not the constraint and was checked rather than assumed:** every
 * block, every face, at 64 px RGBA is about **1.6 MB**, against 0.1 MB at 16.
 *
 * ✅ **AND FRAME RATE IS NOT THE CONSTRAINT EITHER — MEASURED, not assumed.**
 * 🎯 **haugur, 2026-08-13:** *"lenovo reports 60 to 70 fps on the new map."*
 * ⚠️ **That number could only come from him.** This workstation has no GPU and
 * renders in software; it reported **3 fps** on the same build, which is a fact
 * about the VM and about nothing else. **Do not treat a frame rate measured
 * here as evidence of anything.**
 */
export const DEFAULT_TILE = 64;

/**
 * 🌾 **`edge` is a SIDE with grass along its top** — #813. Only a block whose
 * `block-tiles.json` plan names one has it, and the mesher draws it only where
 * nothing covers the block (`edgeLayer`). See {@link EDGE_KEYS}.
 */
export type FaceKind = 'top' | 'side' | 'bottom' | 'edge';

export interface TextureFeature {
  readonly value: number;
  readonly share: number;
  readonly size?: readonly number[];
  readonly hard?: boolean;
  readonly round?: boolean;
  readonly clustered?: boolean;
  readonly horizontal?: boolean;
}

export interface FaceSpec {
  readonly noise?: number;
  readonly features?: readonly TextureFeature[];
  readonly lobes?: { readonly size: readonly number[]; readonly relief: number };
  readonly strokes?: {
    readonly length: readonly number[];
    readonly share: number;
    readonly value: number;
  };
  readonly bands?: { readonly height: readonly number[]; readonly value: number };
}

interface BlockSpec {
  readonly kind?: string;
  readonly fringe?: number;
  readonly top?: FaceSpec;
  readonly side?: FaceSpec;
  readonly all?: FaceSpec;
}

const SPECS: ReadonlyMap<string, BlockSpec> = new Map(
  Object.entries(textureData.blocks as Record<string, BlockSpec>),
);

/** Everything needed to draw one tile, at any resolution. */
export interface TilePlan {
  readonly block: number;
  readonly key: string;
  readonly face: FaceKind;
  readonly hex: string;
  readonly spec?: FaceSpec | undefined;
  /** Rows at the **top** of a side face that are the surface spilling over. */
  readonly fringe?:
    | { readonly rows: number; readonly hex: string; readonly spec?: FaceSpec | undefined }
    | undefined;
  /**
   * How much of this tile is hole, 0…1. 0 for every block but `birkilauf`.
   *
   * Carried on the plan rather than looked up in {@link renderTile}, so the
   * preview tool draws exactly what the game uploads without knowing why.
   */
  readonly cutout: number;
  /**
   * 🌸 **This tile is drawn on a CROSS, so it needs a plant's OUTLINE.**
   *
   * See {@link cutSilhouette}. Carried on the plan for the same reason
   * {@link cutout} is: the preview tool draws what the game uploads without
   * having to know why.
   */
  readonly cross: boolean;
  readonly seed: number;
  /**
   * 🌊 **Which frame of the moving water this is** — T207, `ripple.ts`.
   *
   * Absent on every ordinary tile. Where it is present the tile is the block's
   * own picture **multiplied by the chop field at that frame**, and the plan is
   * one of {@link RIPPLE_FRAMES} appended after all the still tiles.
   *
   * 🔴 **A ripple plan is deliberately NOT in the layer map.** `tileLayer` goes
   * on answering with the still tile, so **the mesher is untouched and every
   * chunk already in memory stays correct** — the shader is what chooses the
   * moving bank. See {@link rippleLayerBase}.
   */
  readonly ripple?: number | undefined;
}

/* ── determinism ──────────────────────────────────────────────────────────── */

/**
 * ⚠️ Seeded, never `Math.random`.
 *
 * The same block must look the same on every machine and after every reload. A
 * texture that differed between two players looking at the same wall would be a
 * bug nobody could reproduce.
 */
function rng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function seedOf(text: string): number {
  let h = 2166136261;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/* ── colour ───────────────────────────────────────────────────────────────── */

export const toLinear = (c: number): number =>
  c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;

export const toSrgb = (c: number): number => {
  const v = c < 0 ? 0 : c > 1 ? 1 : c;
  return v <= 0.0031308 ? v * 12.92 : 1.055 * v ** (1 / 2.4) - 0.055;
};

export function hexToLinear(hex: string): readonly [number, number, number] {
  const n = Number.parseInt(hex.slice(1), 16);
  return [
    toLinear(((n >> 16) & 255) / 255),
    toLinear(((n >> 8) & 255) / 255),
    toLinear((n & 255) / 255),
  ];
}

/* eslint-disable security/detect-object-injection --
   Everything below this line is numeric work over typed arrays: `field` is a
   Float32Array and `out` a Uint8Array, both indexed by loop counters bounded
   above. `security/detect-object-injection` exists to catch prototype
   pollution through plain-object property access, which a typed array cannot
   suffer, and it fires on every element read and write here. Same reasoning
   and same wording as `packages/server/src/roads.ts`, which does raster maths
   for the same reason. */

/* ── the value field ──────────────────────────────────────────────────────── */

/**
 * A brightness multiplier per texel, averaging exactly 1.
 *
 * ⚠️ **The normalisation at the end is load-bearing, not tidiness.** Past about
 * 100 m a tile is smaller than a screen pixel, so the block simply *becomes*
 * this field's mean. A tile that averaged 6% bright would shift its block's
 * value against every other block, at exactly the distance where value is the
 * only thing telling them apart.
 *
 * ⚠️ Sizes in the spec are in texels **at 16**, and are scaled with the tile so
 * a feature keeps its real-world size. A 3-texel crowberry clump is 19 cm
 * whether the tile is 16 or 32.
 */
export function valueField(spec: FaceSpec | undefined, seed: number, tile: number): Float32Array {
  const field = new Float32Array(tile * tile).fill(1);
  if (!spec) return field;

  const random = rng(seed);
  const s = tile / DEFAULT_TILE;
  const scale = (n: number): number => Math.max(1, Math.round(n * s));
  const at = (x: number, y: number): number => ((y + tile) % tile) * tile + ((x + tile) % tile);
  /* Wraps, and keeps the compiler happy about a typed-array read. */
  const mul = (i: number, by: number): void => {
    field[i] = (field[i] ?? 1) * by;
  };

  /* Broad soft horizontal bands — water, and only water. */
  if (spec.bands) {
    const lo = scale(spec.bands.height[0] ?? 3);
    const hi = scale(spec.bands.height[1] ?? 6);
    let y = 0;
    while (y < tile) {
      const h = lo + Math.floor(random() * (hi - lo + 1));
      const v = 1 + (random() * 2 - 1) * spec.bands.value;
      for (let row = y; row < Math.min(tile, y + h); row++) {
        /* Soften the join. The whole point for water is that nothing in the
           tile repeats visibly. */
        const edge = row === y || row === y + h - 1 ? 0.5 : 1;
        for (let x = 0; x < tile; x++) mul(at(x, row), 1 + (v - 1) * edge);
      }
      y += h;
    }
  }

  /* Rounded cushions — mosahraun. Lighter on the crowns, darker in the creases,
     which is what "mjúkar mosaþembur" actually looks like. */
  if (spec.lobes) {
    const lo = (spec.lobes.size[0] ?? 4) * s;
    const hi = (spec.lobes.size[1] ?? 6) * s;
    /* A whole number of periods across the tile, so the pattern wraps without
       a seam when blocks sit side by side. */
    const periods = Math.max(2, Math.round(tile / (lo + (hi - lo) * random())));
    const ox = random() * tile;
    const oy = random() * tile;
    for (let y = 0; y < tile; y++) {
      for (let x = 0; x < tile; x++) {
        const a = Math.sin(((x + ox) / tile) * Math.PI * 2 * periods);
        const b = Math.sin(((y + oy) / tile) * Math.PI * 2 * periods);
        mul(at(x, y), 1 + a * b * spec.lobes.relief);
      }
    }
  }

  /* Fine per-texel grain. */
  if (spec.noise) {
    for (let i = 0; i < field.length; i++) {
      field[i] = (field[i] ?? 1) * (1 + (random() * 2 - 1) * spec.noise);
    }
  }

  /* Vertical strokes — grass and low herbs under birch. The direction is what
     makes it read as growing rather than as speckle. */
  if (spec.strokes) {
    const count = Math.round(tile * tile * spec.strokes.share);
    const lo = scale(spec.strokes.length[0] ?? 2);
    const hi = scale(spec.strokes.length[1] ?? 3);
    for (let i = 0; i < count; i++) {
      const x = Math.floor(random() * tile);
      const y = Math.floor(random() * tile);
      const len = lo + Math.floor(random() * (hi - lo + 1));
      const v = 1 + spec.strokes.value * (random() * 0.6 + 0.7);
      for (let k = 0; k < len; k++) mul(at(x, y + k), v);
    }
  }

  /* Patches — clumps, stones, lichen, clasts, pebbles. */
  for (const f of spec.features ?? []) {
    const lo = scale(f.size?.[0] ?? 1);
    const hi = scale(f.size?.[1] ?? f.size?.[0] ?? 1);
    /* `share` is the fraction of the tile this feature covers, so the count
       follows from the area. That is what lets a published habitat cover table
       be used directly as a recipe. */
    const meanArea = ((lo + hi) / 2) ** 2;
    const count = Math.max(1, Math.round((tile * tile * f.share) / meanArea));

    let cx = random() * tile;
    let cy = random() * tile;
    for (let i = 0; i < count; i++) {
      if (f.clustered && i > 0 && random() < 0.6) {
        /* Crowberry grows in patches, not scattered evenly. */
        cx += (random() * 6 - 3) * s;
        cy += (random() * 6 - 3) * s;
      } else {
        cx = random() * tile;
        cy = random() * tile;
      }

      const w = lo + Math.floor(random() * (hi - lo + 1));
      const h = f.horizontal ? 1 : w;
      const width = f.horizontal ? w + scale(1) : w;
      const strength = f.hard ? 1 : 0.75 + random() * 0.25;

      for (let dy = 0; dy < h; dy++) {
        for (let dx = 0; dx < width; dx++) {
          if (f.round) {
            const rx = (dx - (width - 1) / 2) / (width / 2 || 1);
            const ry = (dy - (h - 1) / 2) / (h / 2 || 1);
            if (rx * rx + ry * ry > 1) continue;
          }
          mul(at(Math.floor(cx) + dx, Math.floor(cy) + dy), 1 + f.value * strength);
        }
      }
    }
  }

  /* ⚠️ Normalise the mean to exactly 1. See the docstring. */
  let sum = 0;
  for (let i = 0; i < field.length; i++) sum += field[i] ?? 1;
  const mean = sum / field.length;
  if (mean > 0) for (let i = 0; i < field.length; i++) field[i] = (field[i] ?? 1) / mean;

  return field;
}

/* ── plans and pixels ─────────────────────────────────────────────────────── */

/**
 * Every tile the palette needs, in a stable order.
 *
 * A **surface** block gets a distinct top and side, and its side carries the
 * fringe. Everything else gets one look on all six faces — which is itself the
 * signal that it is rock rather than something growing.
 */
export function tilePlans(): readonly TilePlan[] {
  const plans: TilePlan[] = [];

  for (const b of BLOCKS) {
    const look = blockAppearance(b.id);
    if (!look) continue;
    const spec = SPECS.get(b.key);

    const push = (
      face: FaceKind,
      hex: string,
      faceSpec: FaceSpec | undefined,
      fringe?: TilePlan['fringe'],
    ): void => {
      plans.push({
        block: b.id,
        key: b.key,
        face,
        hex,
        spec: faceSpec,
        fringe,
        cutout: blockCutout(b.id),
        cross: blockIsCross(b.id),
        seed: seedOf(`${b.key}:${face}`),
      });
    };

    if (spec?.kind === 'surface') {
      push('top', look.top, spec.top);
      /* ⚠️ The fringe goes on the SIDE tile using the TOP colour — that is the
         whole grass-block effect, and without it a stack of two blocks reads as
         two blocks rather than as ground with a cut in it. */
      push('side', look.side, spec.side, { rows: spec.fringe ?? 0, hex: look.top, spec: spec.top });
      push('bottom', look.bottom, spec.side);
    } else {
      const face = spec?.all;
      push('top', look.top, face);
      push('side', look.side, face);
      push('bottom', look.bottom, face);
    }
    /* 🌾 #813 — a grass-band side, for the few blocks that have one. */
    if (EDGE_KEYS.has(b.key))
      push('edge', look.side, spec?.kind === 'surface' ? spec.side : spec?.all);
  }

  /**
   * 🌊 **The moving water, appended after every still tile** — T207.
   *
   * 🔴 **Appended, and that is load-bearing.** Every layer index above is the
   * position of a plan in this array, and the mesher writes those numbers into
   * a vertex attribute. **Inserting anywhere but the end would renumber the
   * blocks after water and draw móberg on the sea**, with nothing to say why.
   *
   * ⚠️ **One bank of frames, not three.** Water's top, side and bottom all
   * resolve to the same authored picture (`vatn-all`), so three banks would be
   * three identical copies of 384 KB.
   */
  const water = BLOCKS.find((b) => b.key === WATER_KEY);
  if (water && blockAppearance(water.id)) {
    const look = blockAppearance(water.id);
    for (let frame = 0; frame < RIPPLE_FRAMES; frame++) {
      plans.push({
        block: water.id,
        key: water.key,
        face: 'top',
        hex: look?.top ?? '#000000',
        spec: SPECS.get(water.key)?.all,
        fringe: undefined,
        cutout: 0,
        cross: false,
        seed: seedOf(`${water.key}:ripple:${String(frame)}`),
        ripple: frame,
      });
    }
  }

  return plans;
}

/** The block whose tile moves. */
const WATER_KEY = 'vatn';

/**
 * 🌊 **The first layer of the moving water**, or `null` if water has no tile.
 *
 * The frames run from here for {@link RIPPLE_FRAMES} layers, in order, so the
 * shader picks `rippleLayerBase() + frame`.
 *
 * ⚠️ **Derived from `tilePlans()` like every other layer index**, so the upload
 * and the shader cannot disagree about where the frames are. That is the same
 * reason {@link tileLayer} is derived rather than written down.
 */
export function rippleLayerBase(): number | null {
  const first = tilePlans().findIndex((p) => p.ripple === 0);
  return first === -1 ? null : first;
}

/**
 * 🌊 **The still layers the moving water stands in for** — water's top, side
 * and bottom, which the mesher writes and the shader redirects.
 *
 * 🔴 **Returned as a list rather than assumed contiguous.** They *are* pushed
 * together today, and a shader that tested `layer >= first && layer <= first+2`
 * would be right until somebody adds a face — at which point it would start
 * animating a neighbouring block's tile and look like a texture bug.
 */
export function rippleStillLayers(): readonly number[] {
  const water = BLOCKS.find((b) => b.key === WATER_KEY);
  if (!water) return [];
  const faces: readonly FaceKind[] = ['top', 'side', 'bottom'];
  return faces.map((face) => tileLayer(water.id, face));
}

/**
 * Which layer of the texture array a block face samples.
 *
 * ⚠️ **Derived from `tilePlans()`, which is why the mesher and the texture
 * upload cannot disagree.** The mesher writes this number into a vertex
 * attribute and the client uploads the layers in the same order, so both read
 * from one list rather than two that have to be kept in step. Getting this
 * wrong would draw móberg on the sea and nothing would say why.
 */
const LAYERS: ReadonlyMap<string, number> = (() => {
  const out = new Map<string, number>();
  for (const [i, p] of tilePlans().entries()) {
    /* 🌊 A ripple frame is not a face anybody can ask for — it would overwrite
       water's still tile with the last frame of its own animation, and the
       mesher would start writing that number into the world. The shader
       chooses the moving bank; see `rippleLayerBase`. */
    if (p.ripple !== undefined) continue;
    out.set(`${p.block}:${p.face}`, i);
  }
  /**
   * 🧱 **T108: a state draws its block's picture.**
   *
   * The atlas is built from the block palette, so a state id has no plan of its
   * own. ⚠️ **Without this loop a lit hearth would fall through to `?? 0`** —
   * layer 0, a real tile belonging to some other block — and it would look like
   * a texture bug rather than a missing lookup. Folding the ids in here rather
   * than resolving inside `tileLayer` keeps this a single map read on a path
   * the mesher walks per face.
   *
   * ⬜ **A lit hearth will eventually want its OWN tile.** When that art exists
   * it becomes a plan in {@link tilePlans} keyed by the state id, and this loop
   * simply stops covering it.
   */
  for (const b of BLOCKS) {
    for (const stateId of statesOf(b.key)) {
      for (const face of ['top', 'side', 'bottom'] as const) {
        const layer = out.get(`${b.id}:${face}`);
        if (layer !== undefined) out.set(`${stateId}:${face}`, layer);
      }
    }
  }
  return out;
})();

/** Which layer of the texture array this face draws from. */
export function tileLayer(block: number, face: FaceKind): number {
  return LAYERS.get(`${block}:${face}`) ?? 0;
}

/**
 * 🌾 **The grass-band side of this block, or `undefined` if it has none** — #813.
 * ⚠️ Unlike {@link tileLayer} this does NOT fall back to layer 0: a block
 * without an edge must keep its plain side, not borrow somebody else's tile.
 */
export function edgeLayer(block: number): number | undefined {
  return LAYERS.get(`${block}:edge`);
}

/* ── authored tiles ───────────────────────────────────────────────────────── */

/**
 * 🔴 **Where a block has a real picture, the picture wins.**
 *
 * `data/block-tiles.json` is generated by `tools/textures/pack.ts` from the
 * images in `art/textures-1024/`. Ten blocks have one; the rest still come from
 * {@link valueField}, and a block with no entry behaves exactly as it did
 * before this existed.
 *
 * ⚠️ **This reverses a documented decision, so it says why.** The client used
 * to argue that generating tiles beat shipping a binary — *"there is no binary
 * asset in git that can silently drift from the data describing it."* **That
 * was right for a texture made of numbers and wrong for one made of art:** the
 * procedural path can only modulate the brightness of a single colour, which is
 * precisely why haugur called the old blocks *"flat, grainy, not visually
 * pleasing."* ✅ **The drift worry is answered rather than dropped** — the
 * sources are committed, the pack is generated from them, and it is never
 * hand-edited.
 *
 * ⚠️ **Raw RGBA rather than PNG** because this package is deliberately
 * zero-dependency and is imported by Node tools as well as the browser. `atob`
 * exists in both; a PNG decoder would have to be written twice.
 */
const AUTHORED: ReadonlyMap<string, Uint8Array> = (() => {
  const out = new Map<string, Uint8Array>();
  if (tileData.$tile !== DEFAULT_TILE) return out;
  const tiles = tileData.tiles as Record<string, string>;
  for (const [key, faces] of Object.entries(
    tileData.faces as Record<string, Record<string, string>>,
  )) {
    for (const [face, name] of Object.entries(faces)) {
      const b64 = tiles[name];
      if (b64 === undefined) continue;
      const bin = atob(b64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      out.set(`${key}:${face}`, bytes);
    }
  }
  return out;
})();

/** The authored tile for a block face, if one has been drawn. */
export function authoredTile(key: string, face: FaceKind): Uint8Array | undefined {
  return AUTHORED.get(`${key}:${face}`);
}

/* ── cutout ───────────────────────────────────────────────────────────────── */

/**
 * How big a hole in a leaf tile is, as a radius in texels at {@link DEFAULT_TILE}.
 *
 * 🔴 **3 texels is about 5 cm of gap on a 1 m block**, and it is the number the
 * first screenshot of T144 sent back for correction. At 1 it was 1.6 cm — **too
 * small to see through and the first thing a mipmap erases**, so the crowns were
 * speckled at arm's length and solid boxes by ten metres.
 *
 * ⚠️ **Bigger holes survive distance; that is not a side effect, it is most of
 * the point.** Each mip level halves the tile, so a gap has to be several texels
 * across to still exist a few levels down — which is where trees are actually
 * looked at.
 */
export const CUTOUT_HOLE_TEXELS = 3;

/**
 * 🍃 **Punch holes in a tile — the alpha channel nothing gave us (T144).**
 *
 * 🔴 **The wrinkle this exists to solve: image generators return OPAQUE
 * images.** There is no alpha to import, and there is no prompt that produces
 * one. So the alpha is **derived from the picture** rather than authored beside
 * it, and it is derived here rather than at pack time so that the amount stays
 * a number in a data file — change `cutout` in `block-behaviour.json`, reload,
 * and the crown thins. **No regeneration, no money, no image editor** (D2).
 *
 * **The rule: the darkest part of a leaf picture is the gap between the
 * leaves.** So the darkest `share` of the tile becomes hole. That is why the
 * holes land where light already falls through rather than through the middle
 * of a leaf, and it is the whole reason this reads as foliage instead of as a
 * green tile with bites taken out of it.
 *
 * ⚠️ **The blur is load-bearing, not smoothing.** Thresholding raw luminance
 * picks the darkest *texels*, which are scattered one-pixel specks — and a
 * speckled alpha at 64 px reads as dirt on the screen, not as a gap. Averaging
 * over each texel's neighbours first picks the darkest *regions*, so the holes
 * come out as a few connected openings. **It wraps**, because the tile does.
 *
 * 🔴 **And the RADIUS of that blur is the whole difference between foliage and
 * static — found by looking, not by reasoning.** The first version used a fixed
 * 3×3 and it failed twice over in one screenshot: up close the crown was
 * speckled like dirt on the lens, and **by about ten metres it had closed back
 * into a solid green box.** Both are the same fault. A one-texel hole is 1.6 cm
 * of gap, which is smaller than anything you could see through, and it is also
 * the first thing a mipmap averages away — **so the holes disappeared at exactly
 * the distance most trees are looked at.** {@link CUTOUT_HOLE_TEXELS} is what
 * sets the scale, and it is in centimetres of real gap rather than in pixels.
 *
 * ⚠️ **This deliberately breaks the mean-value rule for this one block, and
 * that is correct.** `block-textures.json` requires a tile's mean to come out
 * at 1.0, because beyond ~100 m a block simply *is* its mean. Removing the
 * darkest fifth makes the leaves that remain read brighter than they did.
 * **Renormalising them would be wrong** — what is behind a hole is sky or
 * ground, not a darker leaf, so the tile's own average stopped being the whole
 * story the moment it had holes in it.
 *
 * ⚠️ **`share` is a floor, not an exact count.** Texels that tie at the
 * threshold all go, so a picture with large flat areas can lose slightly more
 * than asked. Nothing depends on the exact number.
 */
export function punchCutout(rgba: Uint8Array, tile: number, share: number): Uint8Array {
  const out = rgba.slice();
  if (share <= 0) return out;

  const n = tile * tile;
  /* Scaled with the tile, so a gap keeps its real size in centimetres whatever
     resolution the tiles are built at — the same rule the feature sizes in
     `valueField` follow. */
  const radius = Math.max(1, Math.round((CUTOUT_HOLE_TEXELS * tile) / DEFAULT_TILE));

  /* Rec. 709 luma. The green weight is what matters here — a leaf tile is
     almost entirely green, so anything that treats the channels equally would
     be measuring noise. */
  const lum = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const p = i * 4;
    lum[i] = 0.2126 * (rgba[p] ?? 0) + 0.7152 * (rgba[p + 1] ?? 0) + 0.0722 * (rgba[p + 2] ?? 0);
  }

  /* The neighbourhood average, wrapping at the edges. See the docstring: this
     is what turns "the darkest pixels" into "the darkest places", and the
     radius is what decides how big a place is. */
  const soft = new Float32Array(n);
  const span = radius * 2 + 1;
  for (let y = 0; y < tile; y++) {
    for (let x = 0; x < tile; x++) {
      let sum = 0;
      for (let dy = -radius; dy <= radius; dy++) {
        for (let dx = -radius; dx <= radius; dx++) {
          sum += lum[((y + dy + tile) % tile) * tile + ((x + dx + tile) % tile)] ?? 0;
        }
      }
      soft[y * tile + x] = sum / (span * span);
    }
  }

  /* The `share` quantile. Sorting 4,096 floats once at startup is nothing, and
     it is exact — a guessed brightness threshold would give a different amount
     of hole for every picture. */
  const sorted = Float32Array.from(soft).sort();
  const threshold = sorted[Math.min(n - 1, Math.max(0, Math.round(share * n) - 1))] ?? 0;

  for (let i = 0; i < n; i++) {
    if ((soft[i] ?? 0) <= threshold) out[i * 4 + 3] = 0;
  }
  return out;
}

/**
 * RGBA bytes for one tile, sRGB-encoded and ready for a texture upload.
 *
 * Returns the authored picture where one exists at this tile size, and falls
 * back to the generated field everywhere else. **A cutout block then gets its
 * holes punched** — see {@link punchCutout} — which applies to both paths, so a
 * leaf with no picture yet would still be see-through.
 */
export function renderTile(plan: TilePlan, tile: number = DEFAULT_TILE): Uint8Array {
  const still = renderOpaqueTile(plan, tile);
  /* 🌊 One frame of moving water — the block's own picture with the chop field
     on it. T207, and `ripple.ts` says why it is a multiply rather than a scroll
     or a warp: both of those were built, measured and thrown away. */
  const opaque = plan.ripple === undefined ? still : rippleFrame(still, tile, plan.ripple);
  const holes = plan.cutout > 0 ? punchCutout(opaque, tile, plan.cutout) : opaque;
  return plan.cross ? cutSilhouette(holes, tile) : holes;
}

/**
 * 🌸 **Cut a tile down to the OUTLINE OF A PLANT** — everything outside it goes
 * transparent. T159, 2026-09-09.
 *
 * 🔴 **This is the difference between a bush and a piece of torn cardboard, and
 * it was found by baking a picture.** `kjarr`'s tile was authored as a **ground
 * carpet** — *"foliage on all six faces, because a bush has no top and sides"* —
 * which is exactly right for a squashed cube and exactly wrong on a cross.
 * Punching holes in mottled noise gives a **moth-eaten rectangle**: the geometry
 * was correct and the patch still looked like litter.
 *
 * ## The shape, and why it is this one
 *
 * **A tuft: narrow at the foot, spreading upward, ragged at the edge.** That is
 * what scrub does and it is what Minecraft's short grass draws. ⚠️ **The ragged
 * edge matters more than the taper** — a clean triangle reads as a cardboard
 * cut-out, which is the fault this function exists to avoid, so the boundary is
 * pushed in and out by the tile's own noise.
 *
 * ⚠️ **It multiplies the alpha that is already there**, so the holes punched
 * between leaf clumps survive. Two different see-through rules, one channel.
 *
 * 🔴 **The tile still has to TILE for anything else that uses it.** Nothing
 * does today — `kjarr` is the only cross — but a block drawn as both a cross
 * and a surface would need two tiles, not one, and this would silently ruin the
 * surface. **That is a real limit and it is written here rather than discovered.**
 */
export function cutSilhouette(rgba: Uint8Array, tile: number): Uint8Array {
  const out = rgba.slice();
  for (let y = 0; y < tile; y++) {
    /* 0 at the top of the tile, 1 at the foot — v runs down, as the mesh does. */
    const down = (y + 0.5) / tile;
    /**
     * How much of the width is plant at this height. **A stem at the bottom and
     * a spread at the top**, and the top is not full width either: a tuft is
     * rounded, not a fan.
     */
    const spread = 0.22 + 0.78 * Math.sin(Math.min(1, down * 1.15) * Math.PI * 0.62);
    for (let x = 0; x < tile; x++) {
      const p = (y * tile + x) * 4;
      const across = Math.abs((x + 0.5) / tile - 0.5) * 2;
      /* 🔴 The edge is pushed about by the tile's own luminance, so the outline
         is ragged rather than drawn with a compass. Same bytes every time —
         it is the picture's own noise, not a random number (D2). */
      const lum = ((rgba[p] ?? 0) + (rgba[p + 1] ?? 0) + (rgba[p + 2] ?? 0)) / 765;
      const edge = spread + (lum - 0.5) * 0.34;
      if (across > edge) out[p + 3] = 0;
    }
  }
  return out;
}

function renderOpaqueTile(plan: TilePlan, tile: number): Uint8Array {
  if (tile === DEFAULT_TILE) {
    const drawn = AUTHORED.get(`${plan.key}:${plan.face}`);
    if (drawn) return drawn;
  }
  return renderGeneratedTile(plan, tile);
}

/** The procedural tile — numbers rather than a picture. */
function renderGeneratedTile(plan: TilePlan, tile: number = DEFAULT_TILE): Uint8Array {
  const out = new Uint8Array(tile * tile * 4);
  const base = hexToLinear(plan.hex);
  const field = valueField(plan.spec, plan.seed, tile);

  const fringeRows = plan.fringe ? Math.round(plan.fringe.rows * (tile / DEFAULT_TILE)) : 0;
  const fringeBase = plan.fringe ? hexToLinear(plan.fringe.hex) : undefined;
  /* A different seed, or the moss on the side would line up with the moss on
     the top and the corner would read as a mirror. */
  const fringeField = plan.fringe
    ? valueField(plan.fringe.spec, plan.seed ^ 0x9e3779b9, tile)
    : undefined;

  for (let y = 0; y < tile; y++) {
    const inFringe = y < fringeRows;
    const colour = inFringe && fringeBase ? fringeBase : base;
    const values = inFringe && fringeField ? fringeField : field;

    for (let x = 0; x < tile; x++) {
      const i = y * tile + x;
      const v = values[i] ?? 1;
      const p = i * 4;
      out[p] = Math.round(toSrgb(colour[0] * v) * 255);
      out[p + 1] = Math.round(toSrgb(colour[1] * v) * 255);
      out[p + 2] = Math.round(toSrgb(colour[2] * v) * 255);
      out[p + 3] = 255;
    }
  }
  return out;
}
